<template>
  <!-- Shaft element member detail panel (right panel) -->
  <HeaderBar :modifiers="['dark', 'sticky']" class="hide-for-medium">
    <router-link
      class="header-bar__title typo-nav-item"
      :to="{ name: 'shaftScroll', params: { elementType: elementType } }"
      :aria-label="$t('general.back')"
    >
      <UseSvg name="arrow_left" class="icon" />
      <div class="typo-small-title">
        {{ $t('shafts.condition.member_details') }}
      </div>
    </router-link>
  </HeaderBar>
  <!-- Shaft cross section -->
  <div class="condition-layout__right-content">
    <div v-if="!allHistoryVisible && shaftDiagramVisible" class="diagram">
      <ShaftDiagram
        v-if="shaftDiagramData"
        :elements="shaftDiagramData.elements"
        :shape="shaftDiagramData.shape"
        :activeItem="shaftDiagramData.activeItem || activeDiagram"
      />
    </div>

    <!-- Member details -->
    <div class="member_details">
      <div v-if="$route.params.memberId && $route.params.memberId.includes('no_detail')">
        <EmptyMessage :message="$t('shafts.condition.no_detmechs')">
        </EmptyMessage>
      </div>
      <Loader v-if="loading" />
      <template v-if="memberDetail && !loading">
        <div v-if="!allHistoryVisible" class="member_details__info">
          <div class="member_details__info-left typo-small-title">
            <strong>{{ $t('shafts.condition.member_details') }}</strong>
            <div class="member_details__info-location member_details__info--dark member_details__info--margin typo-small">
              {{ memberDetail.location_name }}
            </div>
            <div class="typo-tiny member_details__info-date">
              <div class="typo-tiny">
                {{ formatDate(memberDetail.updated_at) }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="!allHistoryVisible" class="member_details__content">
          <div class="member_details__content-row">
            <div class="member_details__content-row-left typo-small">
              {{ $t('shafts.condition.condition') }}
            </div>
            <div
              :class="`typo-tiny member_details__content-row-right member_details__condition member_details__condition--${memberDetail.overall_condition_rating}`"
            >
              {{ memberDetail.overall_condition_rating }}
            </div>
          </div>

          <DetmechAccordion
            v-if="memberDetail.deterioration_mechanism_ratings.length > 0"
            :title="$t('shafts.condition.deterioration_mechanisms')"
            :detmechs="memberDetail.deterioration_mechanism_ratings"
            :isOpen="true"
            :hasTrigger="false"
          />

          <div
            class="member_details__warnings"
            v-if="memberDetail.warnings.length > 0"
          >
            {{ $t('shafts.condition.warnings') }}
          </div>
        </div>

        <!-- History Header -->
        <div class="member_details__heading">
          <div v-if="!allHistoryVisible" class="member_details__heading-left typo-small-title">
            {{ $t('shafts.condition.member_history') }}
          </div>
          <div
            :class="{
              'sidebar-item__section-link': true,
              'sidebar-item__section-link--back': allHistoryVisible,
            }"
            @click="handleSeeAllHistory"
          >
            {{ allHistoryVisible ? $t('shafts.condition.member_history') : linkLabel }}
            <UseSvg :name="iconDirection" class="icon icon--small" />
          </div>
        </div>

        <!-- history items -->
        <Loader v-if="historyLoading" />
        <MemberDetailsHistory
          v-if="historyData && !historyLoading"
          :data="historyData"
        />
      </template>
    </div>
    <EmptyMessage
      :title="$t('shafts.condition.select_a_member')"
      v-if="!memberDetail && !loading && !$route.params.memberId"
    >
      <CircleSquareTargetSvg />
    </EmptyMessage>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import { formatDate } from '@/functions/date_format_helpers';
import I18n from 'i18n-js';

import DetmechAccordion from '@/components/shafts/condition/detmech_accordion.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import MemberDetailsHistory from '@/components/shafts/condition/member_details_history.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import ShaftDiagram from '@/components/shafts/shaft_diagram.vue';

import CircleSquareTargetSvg from '@/images/circle-square-target.svg';

const props = defineProps({
  elementType: { type: String, required: true },
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  memberId: { type: String, required: false, default: null },
  shaftDiagramData: { type: Object, required: false, default: null },
  shaftDiagramVisible: { type: Boolean, default: false },
});

const route = useRoute();
const ajax = useAjax();
const loading = ref(false);
const historyLoading = ref(false);
const memberDetail = ref(null);
const allHistory = ref(null);
const latestMemberActivity = ref(null);
const allHistoryVisible = ref(null);
const activeDiagram = ref(null);

/**
 * Returns all history or latest activity
 */
const historyData = computed(() => {
  return allHistoryVisible.value
    ? allHistory.value
    : latestMemberActivity.value;
});

/**
 * Sets the label link based see all state
 */
const linkLabel = computed(() => {
  return I18n.t('general.see_all');
});

/**
 * Flips the icon when see all is active
 */
const iconDirection = computed(() =>
  allHistoryVisible.value ? 'arrow_left_16' : 'arrow_right_16'
);

onMounted(() => {
  loadMemberDetail();
});

watch(() => route.params.memberId, loadMemberDetail);

async function loadMemberDetail() {
  loading.value = true;

  if (
    route.params.memberId &&
    !route.params.memberId.includes('no_detail')
  ) {
    let { ok, data } = await ajax(
      `/organisations/${props.organisationId}/shafts/${props.shaftId}/members/${route.params.memberId}`
    );

    if (ok) {
      memberDetail.value = data;
      latestMemberActivity.value = [data.latest_activity];
      activeDiagram.value = data.active_diagram;
    }
  } else {
    memberDetail.value = null;
    activeDiagram.value = null;
  }

  allHistoryVisible.value = false;
  allHistory.value = null;
  loading.value = false;
}

/**
 * Gets the full history for the member
 */
async function loadFullHistory() {
  historyLoading.value = true;

  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/shafts/${props.shaftId}/members/${route.params.memberId}/activities`
  );

  if (ok) {
    allHistory.value = data;
  }

  historyLoading.value = false;
}

/**
 * Handles see all toggle and data call
 */
function handleSeeAllHistory() {
  allHistoryVisible.value = !allHistoryVisible.value;

  if (allHistoryVisible.value) {
    loadFullHistory();
  }
}
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';

.member_details {
  $block: &;

  margin-top: bu(3);

  &__info {
    border-bottom: 0.5px solid map-get($foreground-color, darker);
    display: flex;
    justify-content: space-between;
    padding: bu(1.75);

    &--dark {
      color: map-get($foreground-color, dark);
    }

    &--margin {
      margin-top: bu(1);
    }
  }

  &__info-date {
    color: map-get($foreground-color, dark);
    margin-top: bu(1.5);
  }

  &__heading {
    border-bottom: 0.5px solid map-get($foreground-color, darker);
    display: flex;
    justify-content: space-between;
    padding: bu(1.75);
  }

  &__content {
    padding: bu(2) bu(2);
  }

  &__content-row {
    display: flex;
    justify-content: space-between;
  }

  &__condition {
    align-items: center;
    background-color: map-get($background-color, lightest);
    border-radius: map-get($border-radius, light);
    color: map-get($foreground-color, light);
    display: flex;
    font-weight: 500;
    height: bu(2.5);
    justify-content: center;
    width: bu(2.5);

    @each $rating, $color in $condition-rating-color {
      &--#{$rating} {
        background-color: map-get($color, default);
      }
    }
  }
}
</style>
